import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Heading, Text, useResponsive } from '@common-fe/common-fe';
import { Option } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import AppButton from '@/components/controls/AppButton';
import { StyledSelectDropdown } from '@/modules/transaction/components/CardDetails/CardDetails.styles';
import { ROOT_DEPENDENT_ID } from '@/modules/transaction/components/Cards/Cards.constants.ts';
import { useGetCardsQuery } from '@/modules/transaction/queries/useGetCards.query';
import { useAuthStore } from '@/modules/user/stores';

const QUERY_KEY = 'dependent_id';

export const CardDetailsHeader = () => {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location?.search || ''), [location]);
  const { user } = useAuthStore();
  const { holderList } = useGetCardsQuery(user?.employee?.id);
  const dependentId = useMemo(() => query.get(QUERY_KEY) || ROOT_DEPENDENT_ID, [query]);
  const holderFirstName = useMemo(
    () => holderList.find((holder) => holder.dependentId === dependentId)?.firstName || '',
    [holderList, dependentId]
  );
  const history = useHistory();
  const holdersOptions: Option[] = useMemo(
    () =>
      holderList.map((holder) => ({
        key: holder.dependentId || ROOT_DEPENDENT_ID,
        value: holder.dependentId || ROOT_DEPENDENT_ID,
        title: holder.name,
      })),
    [holderList]
  );
  const activeHolder = useMemo(
    () => holderList.find((holder) => holder.dependentId === dependentId)?.dependentId || '',
    [holderList, dependentId]
  );

  const { isMobile } = useResponsive();

  return (
    <Box
      margin={{ bottom: isMobile ? 'spacing12' : 'spacing32', horizontal: isMobile ? 'spacing16' : '' }}
      direction={isMobile ? 'column' : 'row'}
      justify="between"
      align={isMobile ? 'start' : 'center'}
      data-testid="CardDetailsHeader-wrapper"
    >
      <Heading level={1} size="mobile" data-testid="CardDetailsHeader-holder-name">
        {holderFirstName}'s Cards
      </Heading>

      <Box
        fill={isMobile}
        direction={isMobile ? 'column' : 'row'}
        margin={isMobile ? { top: 'spacing12' } : {}}
        justify="between"
        align="center"
      >
        <Box
          fill={isMobile}
          margin={isMobile ? { bottom: 'spacing12' } : { right: 'spacing12' }}
          data-testid="CardDetailsHeader-holder-select"
          width={{ min: '160px' }}
        >
          <StyledSelectDropdown
            id="cardholder-select"
            options={holdersOptions}
            singleMode
            values={activeHolder ? [activeHolder] : []}
            placeholder="Select a value"
            name="dsa"
            onChangeValues={(value) =>
              history.push(
                `${ROUTES.CARD_DETAILS}?dependent_id=${value.toString().replace(`${dependentId},`, '')}`
              )}
          />
        </Box>
        <AppButton
          fill={isMobile}
          buttonType="secondary"
          onClick={() => history.push(ROUTES.CARDS)}
          testId="CardDetailsHeader-manage-cards"
        >
          Manage Cards
        </AppButton>
      </Box>
    </Box>
  );
};
